import { useRef, useEffect, useCallback, useState } from 'react';
import { Stream } from '@cloudflare/stream-react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize } from 'lucide-react';

// Official Cloudflare Stream player for live inputs with a CLEAN custom control
// bar (play/pause, volume, fullscreen only). Cloudflare's built-in controls are
// disabled so there is NO settings/quality gear and NO live indicator.
// Cloudflare still handles HLS/LL-HLS, buffering recovery and reconnects natively.
// `src` must be the Cloudflare video/live-input UID.
export default function CloudflareLivePlayer({ src }) {
  const apiRef = useRef(null);
  const wrapRef = useRef(null);
  const hideTimer = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);    // bump to remount/refresh the iframe
  const reloadingRef = useRef(false);

  const unmute = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    try {
      api.muted = false;
      api.volume = 1;
      const p = api.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => { /* ignore */ });
    } catch { /* ignore */ }
  }, []);

  // First interaction anywhere → unmute to full volume.
  useEffect(() => {
    const handler = () => unmute();
    document.addEventListener('pointerdown', handler);
    document.addEventListener('touchstart', handler);
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('keydown', handler);
    };
  }, [unmute]);

  // Guarantee playback: if unmuted autoplay was blocked and we're still paused
  // shortly after load, start muted so the video plays; listeners unmute on tap.
  useEffect(() => {
    const t = setTimeout(() => {
      const api = apiRef.current;
      try {
        if (api && api.paused) {
          api.muted = true;
          const p = api.play?.();
          if (p && typeof p.catch === 'function') p.catch(() => { /* ignore */ });
        }
      } catch { /* ignore */ }
    }, 2500);
    return () => clearTimeout(t);
  }, [src]);

  // Track fullscreen changes (incl. ESC exit); unlock orientation when leaving.
  useEffect(() => {
    const onFs = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setFullscreen(fs);
      if (!fs) { try { window.screen?.orientation?.unlock?.(); } catch { /* ignore */ } }
    };
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (apiRef.current && !apiRef.current.paused) setControlsVisible(false);
    }, 3000);
  }, []);

  const togglePlay = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    try { if (api.paused) api.play?.().catch(() => { /* ignore */ }); else api.pause?.(); } catch { /* ignore */ }
    revealControls();
  }, [revealControls]);

  const toggleMute = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const next = !api.muted;
    try { api.muted = next; if (!next && api.volume === 0) { api.volume = 1; setVolume(1); } } catch { /* ignore */ }
    setMuted(next);
    revealControls();
  }, [revealControls]);

  const onVolumeSlider = useCallback((e) => {
    const v = Number(e.target.value);
    const api = apiRef.current;
    try { if (api) { api.volume = v; api.muted = v === 0; } } catch { /* ignore */ }
    setVolume(v);
    setMuted(v === 0);
    revealControls();
  }, [revealControls]);

  const toggleFullscreen = useCallback(() => {
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
        try { window.screen?.orientation?.unlock?.(); } catch { /* ignore */ }
      } else {
        const el = wrapRef.current;
        const req = el?.requestFullscreen?.() || el?.webkitRequestFullscreen?.();
        // On mobile (touch + small screen), lock to landscape for an immersive
        // viewing experience. On laptop/desktop/TV this is a no-op (left as-is).
        const isMobile = (typeof window !== 'undefined') &&
          (window.matchMedia?.('(pointer: coarse)')?.matches) &&
          Math.min(window.innerWidth, window.innerHeight) < 820;
        Promise.resolve(req).then(() => {
          if (isMobile) { try { window.screen?.orientation?.lock?.('landscape').catch(() => {}); } catch { /* ignore */ } }
        }).catch(() => { /* ignore */ });
      }
    } catch { /* ignore */ }
    revealControls();
  }, [revealControls]);

  const syncFromApi = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    try { setPlaying(!api.paused); setMuted(!!api.muted); if (typeof api.volume === 'number') setVolume(api.volume); }
    catch { /* ignore */ }
  }, []);

  // Tap on the video re-reveals the control bar (the cross-origin iframe swallows
  // taps, so we capture them on an overlay above it); they auto-hide again after 3s.
  const toggleControls = useCallback(() => { revealControls(); }, [revealControls]);

  // Resume playback reliably. Cloudflare/browser policy REJECTS play() on an
  // unmuted video that wasn't user-initiated — so if the unmuted resume is
  // blocked, we resume MUTED (always allowed) and re-arm unmute on next tap.
  // Guarantees a live broadcast never stays paused.
  const resume = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    try {
      const p = api.play?.();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          // Unmuted resume blocked by browser policy -> resume muted (always
          // allowed). The always-on interaction listener re-unmutes on next tap.
          try {
            api.muted = true;
            const p2 = api.play?.();
            if (p2 && typeof p2.catch === 'function') p2.catch(() => { /* ignore */ });
          } catch { /* ignore */ }
        });
      }
    } catch { /* ignore */ }
  }, []);

  // Auto-resume immediately whenever the live video pauses or stalls.
  const handlePause = useCallback(() => {
    setPlaying(false);
    setControlsVisible(true);
    resume();
  }, [resume]);

  // Refresh: remount the Cloudflare iframe to recover from a hard error/stall.
  const forceReload = useCallback(() => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    setReloadKey((k) => k + 1);
    setTimeout(() => { reloadingRef.current = false; }, 8000);
  }, []);

  const handleError = useCallback(() => { forceReload(); }, [forceReload]);

  // Watchdog: a live broadcast should never sit paused. Every 2.5s, if it's
  // paused, resume automatically (with the muted fallback above).
  useEffect(() => {
    const t = setInterval(() => {
      const api = apiRef.current;
      if (!api) return;
      try { if (api.paused) resume(); } catch { /* ignore */ }
    }, 2500);
    return () => clearInterval(t);
  }, [resume]);

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 bg-black group"
      onContextMenu={(e) => e.preventDefault()}
      onMouseMove={revealControls}
      data-testid="cf-live-player"
    >
      <Stream
        key={reloadKey}
        streamRef={apiRef}
        src={src}
        controls={false}
        autoplay
        muted={false}
        preload="auto"
        className="w-full h-full"
        height="100%"
        width="100%"
        responsive={false}
        onCanPlay={() => { unmute(); syncFromApi(); }}
        onPlay={() => { setPlaying(true); revealControls(); }}
        onPause={handlePause}
        onError={handleError}
        onStalled={handlePause}
        onVolumeChange={syncFromApi}
      />

      {/* Transparent tap layer — reopens the controls after they auto-hide */}
      <button
        type="button"
        aria-label="Toggle controls"
        onClick={toggleControls}
        className="absolute inset-0 z-10 w-full h-full bg-transparent cursor-pointer"
        data-testid="cf-tap-layer"
      />

      {/* Custom control bar — play/pause, volume, fullscreen only */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-20 flex items-center gap-3 px-4 py-3 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-300 ${controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        data-testid="cf-controls"
      >
        <button onClick={togglePlay} className="text-white hover:text-rose-400 transition-colors" title={playing ? 'Pause' : 'Play'} data-testid="cf-play-toggle">
          {playing ? <Pause className="w-6 h-6" fill="currentColor" /> : <Play className="w-6 h-6" fill="currentColor" />}
        </button>

        <button onClick={toggleMute} className="text-white hover:text-rose-400 transition-colors" title={muted ? 'Unmute' : 'Mute'} data-testid="cf-mute-toggle">
          {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <input
          type="range" min="0" max="1" step="0.05"
          value={muted ? 0 : volume}
          onChange={onVolumeSlider}
          className="w-24 accent-rose-500 cursor-pointer"
          title="Volume"
          data-testid="cf-volume"
        />

        <div className="flex-1" />

        <button onClick={toggleFullscreen} className="text-white hover:text-rose-400 transition-colors" title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} data-testid="cf-fullscreen-toggle">
          {fullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
